import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import SEO from "../components/SEO";
import LandingNav from "../components/Layout/LandingNav";
import LandingFooter from "../components/Layout/LandingFooter";

// EVERY claim on this page must be verifiable in the codebase or from a named
// subprocessor. No certifications (SOC 2, ISO 27001, HIPAA), no uptime guarantees,
// no "bank-grade"/"military-grade" language. If a claim cannot be pointed at a
// specific implementation, it does not belong here.
//
// Verified when written (Aug 2026):
//   bcrypt password hashing ....... backend/models/User.js:10
//   HTTP security headers + CSP ... backend/server.js:67
//   API rate limiting ............. backend/server.js:82
//   Customer deletes own sources .. backend/routes/sources.js:41
//   Session revocation ............ backend/routes/users.js:19
//   Branding gate (server-side) ... backend/controllers/publicWidgetController.js:126

const SECTIONS = [
    {
        title: "Where your data lives",
        body: [
            "Your account, widget settings and conversation history are stored in Supabase, a managed PostgreSQL platform. The content you upload for training is processed into vector embeddings and stored in Qdrant, a vector database.",
            "Traffic between your browser, your website widget and our servers is served over HTTPS.",
        ],
    },
    {
        title: "What the chatbot is allowed to answer from",
        body: [
            "Your chatbot answers from the sources you add: uploaded files, pasted text, manual question-and-answer pairs, and pages scraped from a website URL you provide. It is not trained on other customers' content, and one customer's chatbot cannot read another's sources.",
            "When the chatbot cannot find relevant information in your sources, it says so rather than inventing an answer, and can point the visitor to your support contact. You can edit that fallback message. We do not claim the model is incapable of error — no honest provider can.",
        ],
    },
    {
        title: "Accounts and access",
        body: [
            "Passwords are hashed with bcrypt before storage. We never store them in a form we can read, which also means we cannot tell you your password — only help you reset it.",
            "Dashboard requests are authenticated with short-lived access tokens. You can see your active sessions and revoke any of them from your dashboard, which signs that device out.",
            "The API sits behind rate limiting and standard HTTP security headers, including a content security policy.",
        ],
    },
    {
        title: "Payments",
        body: [
            "Billing is handled by Lemon Squeezy, acting as merchant of record. Card details are entered on their checkout and never reach PaperBrainz servers — we store only the resulting subscription status, plan and billing period.",
        ],
    },
    {
        title: "Deleting your data",
        body: [
            "You can delete any knowledge source from your dashboard at any time. Deleting a source removes it from the chatbot's available knowledge.",
            "Account closure is not yet self-service. Email support@paperbrainz.com from your account address and we will delete your account and its associated data.",
        ],
    },
    {
        title: "Subprocessors",
        body: [
            "We rely on a small number of third-party providers to run the service. If you need a current list for a vendor review, email support@paperbrainz.com and we will provide it in writing.",
        ],
    },
    {
        title: "Reporting a security problem",
        body: [
            "If you believe you have found a vulnerability, email support@paperbrainz.com with the subject line \"Security\". Please include enough detail to reproduce the issue. We will confirm receipt and keep you updated while we investigate, and we will not pursue action against good-faith research that avoids privacy violations, data destruction and service disruption.",
        ],
    },
    {
        title: "What we do not claim",
        body: [
            "PaperBrainz is a small product and we would rather be straight with you than impressive. We do not hold SOC 2, ISO 27001 or HIPAA certification, and we do not offer a contractual uptime guarantee. If your organisation requires any of those, we are probably not the right fit yet — and we would rather tell you now than after you have paid.",
        ],
    },
];

function SecurityPage() {
    const { theme } = useTheme();
    const d = theme === "dark";

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://paperbrainz.com/" },
                    { "@type": "ListItem", position: 2, name: "Security", item: "https://paperbrainz.com/security" },
                ],
            },
            {
                "@type": "WebPage",
                name: "Security and data handling",
                url: "https://paperbrainz.com/security",
                description:
                    "How PaperBrainz stores, protects and deletes your data: hosting, access controls, payments, deletion and how to report a security issue.",
            },
        ],
    };

    return (
        <div className={`min-h-screen flex flex-col ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            <SEO
                title="Security"
                description="How PaperBrainz stores, protects and deletes your data — hosting, access controls, payment handling, data deletion, and how to report a security issue. Plainly stated, with no certification claims we do not hold."
                path="/security"
                jsonLd={jsonLd}
            />
            <LandingNav />

            <div className="flex-1 pt-32 sm:pt-40 pb-24 sm:pb-32 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-16 max-w-2xl mx-auto">
                        <h1 className={`text-5xl md:text-6xl font-semibold tracking-tight mb-5 ${d ? "text-white" : "text-gray-900"}`}>
                            Security
                        </h1>
                        <p className={`text-lg md:text-xl font-light leading-relaxed ${d ? "text-gray-500" : "text-gray-500"}`}>
                            You are about to let a chatbot speak to your customers using your own content. Here is exactly how that
                            content is handled — including the things we cannot yet promise.
                        </p>
                    </div>

                    <div className="space-y-12">
                        {SECTIONS.map((section) => (
                            <section key={section.title}>
                                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? "text-white" : "text-gray-900"}`}>
                                    {section.title}
                                </h2>
                                {section.body.map((para, i) => (
                                    <p key={i} className={`mb-4 font-light leading-relaxed ${d ? "text-gray-400" : "text-gray-600"}`}>
                                        {para}
                                    </p>
                                ))}
                            </section>
                        ))}
                    </div>

                    <div className={`mt-16 pt-8 border-t text-sm font-light ${d ? "border-white/[0.06] text-gray-500" : "border-gray-200 text-gray-500"}`}>
                        <p>
                            See also our{" "}
                            <Link to="/privacy-policy" className="underline hover:no-underline">Privacy Policy</Link>,{" "}
                            <Link to="/terms-of-service" className="underline hover:no-underline">Terms of Service</Link> and{" "}
                            <Link to="/refund-policy" className="underline hover:no-underline">Refund Policy</Link>. Questions about any of
                            this? Email{" "}
                            <a href="mailto:support@paperbrainz.com" className="underline hover:no-underline">support@paperbrainz.com</a>.
                        </p>
                    </div>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
}

export default SecurityPage;
